/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * The latest recorded decision still standing for this server, independent of the request's lifecycle status — a reopened request's prior decision keeps enforcing until re-decided, and clients checking an edit against standing intent must read this rather than status. Absent when nothing was ever decided or the decision was superseded.
 */
export const StandingDecision = {
  Approved: "approved",
  Denied: "denied",
} as const;
/**
 * The latest recorded decision still standing for this server, independent of the request's lifecycle status — a reopened request's prior decision keeps enforcing until re-decided, and clients checking an edit against standing intent must read this rather than status. Absent when nothing was ever decided or the decision was superseded.
 */
export type StandingDecision = ClosedEnum<typeof StandingDecision>;

/**
 * superseded means the latest decision was explicitly displaced by a policy URL-list edit: the history is preserved but no enforcement derives from it until someone re-decides.
 */
export const Status = {
  Unreviewed: "unreviewed",
  Requested: "requested",
  Approved: "approved",
  Denied: "denied",
  Superseded: "superseded",
} as const;
/**
 * superseded means the latest decision was explicitly displaced by a policy URL-list edit: the history is preserved but no enforcement derives from it until someone re-decides.
 */
export type Status = ClosedEnum<typeof Status>;

/**
 * The MCP approval request tracking review status for a server. Status records the review outcome, which may cover only selected principals; the server's access field reports enforcement state.
 */
export type ShadowMCPInventoryApprovalRequest = {
  /**
   * When the daily recheck first found the permission-relevant evidence differing from what the latest approval rested on. Absent when nothing has drifted; cleared only by a new decision.
   */
  evidenceChangedAt?: Date | undefined;
  id: string;
  /**
   * How many distinct people have asked for this server.
   */
  requesterCount: number;
  /**
   * The latest recorded decision still standing for this server, independent of the request's lifecycle status — a reopened request's prior decision keeps enforcing until re-decided, and clients checking an edit against standing intent must read this rather than status. Absent when nothing was ever decided or the decision was superseded.
   */
  standingDecision?: StandingDecision | undefined;
  /**
   * superseded means the latest decision was explicitly displaced by a policy URL-list edit: the history is preserved but no enforcement derives from it until someone re-decides.
   */
  status: Status;
};

/** @internal */
export const StandingDecision$inboundSchema: z.ZodMiniEnum<
  typeof StandingDecision
> = z.enum(StandingDecision);

/** @internal */
export const Status$inboundSchema: z.ZodMiniEnum<typeof Status> = z.enum(
  Status,
);

/** @internal */
export const ShadowMCPInventoryApprovalRequest$inboundSchema: z.ZodMiniType<
  ShadowMCPInventoryApprovalRequest,
  unknown
> = z.pipe(
  z.object({
    evidence_changed_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    id: z.string(),
    requester_count: z.int(),
    standing_decision: z.optional(StandingDecision$inboundSchema),
    status: Status$inboundSchema,
  }),
  z.transform((v) => {
    return remap$(v, {
      "evidence_changed_at": "evidenceChangedAt",
      "requester_count": "requesterCount",
      "standing_decision": "standingDecision",
    });
  }),
);

export function shadowMCPInventoryApprovalRequestFromJSON(
  jsonString: string,
): SafeParseResult<ShadowMCPInventoryApprovalRequest, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ShadowMCPInventoryApprovalRequest$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ShadowMCPInventoryApprovalRequest' from JSON`,
  );
}
